import { ArrowClockwise, Bell, Checks } from '@phosphor-icons/react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../lib/api'
import { NOTIFICATION_CHECK_INTERVAL_MS, shouldCheckNotifications } from '../lib/notification-schedule'
import type { AppNotification, NotificationList, SessionContext } from '../lib/types'
import { Button, Drawer } from './common'

const emptyNotifications: NotificationList = {
  items: [],
  unreadCount: 0,
  checkedAt: '',
  preferences: { notifyOwnChanges: false },
}

function normalizeNotificationList(value: Partial<NotificationList> | null | undefined): NotificationList {
  return {
    items: Array.isArray(value?.items) ? value.items : [],
    unreadCount: Number(value?.unreadCount ?? 0),
    checkedAt: typeof value?.checkedAt === 'string' ? value.checkedAt : '',
    preferences: value?.preferences ?? emptyNotifications.preferences,
  }
}

function readNotificationCache(key: string) {
  try {
    return normalizeNotificationList(JSON.parse(window.localStorage.getItem(key) ?? 'null'))
  } catch {
    return emptyNotifications
  }
}

function notificationTime(value: string) {
  return new Intl.DateTimeFormat('zh-TW', {
    timeZone: 'Asia/Taipei',
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(new Date(value))
}

export function NotificationCenter({ session, isCollapsed }: { session: SessionContext; isCollapsed: boolean }) {
  const navigate = useNavigate()
  const lastCheckedKey = `ess-notifications-last-check:${session.user.id}`
  const cacheKey = `ess-notifications-cache:${session.user.id}`
  const channelName = `ess-notifications:${session.user.id}`
  const lockName = `ess-notifications-check:${session.user.id}`
  const [open, setOpen] = useState(false)
  const [data, setData] = useState<NotificationList>(() => readNotificationCache(cacheKey))
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState('')
  const channelRef = useRef<BroadcastChannel | null>(null)
  const initializedRef = useRef(false)

  const storeData = useCallback((next: NotificationList) => {
    setData(next)
    window.localStorage.setItem(cacheKey, JSON.stringify(next))
  }, [cacheKey])

  const fetchNotifications = useCallback(async (force = false) => {
    const now = new Date()
    const lastCheckedAt = window.localStorage.getItem(lastCheckedKey)
    if (!shouldCheckNotifications(now, lastCheckedAt, force)) return

    const execute = async () => {
      if (!force) {
        const latestCheckedAt = window.localStorage.getItem(lastCheckedKey)
        if (!shouldCheckNotifications(new Date(), latestCheckedAt)) return
      }
      setBusy(true)
      try {
        const next = await api.notifications()
        storeData(next)
        window.localStorage.setItem(lastCheckedKey, next.checkedAt)
        channelRef.current?.postMessage(next)
        setMessage('')
      } catch (error) {
        setMessage(error instanceof Error ? error.message : '通知載入失敗。')
      } finally {
        setBusy(false)
      }
    }

    if (!force && navigator.locks) {
      await navigator.locks.request(lockName, { ifAvailable: true }, async (lock) => {
        if (lock) await execute()
      })
      return
    }
    await execute()
  }, [lastCheckedKey, lockName, storeData])

  useEffect(() => {
    const channel = new BroadcastChannel(channelName)
    channelRef.current = channel
    channel.onmessage = (event: MessageEvent<Partial<NotificationList>>) => storeData(normalizeNotificationList(event.data))
    const needsInitialFetch = !initializedRef.current && !window.localStorage.getItem(cacheKey)
    initializedRef.current = true
    void fetchNotifications(needsInitialFetch)

    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') void fetchNotifications()
    }, NOTIFICATION_CHECK_INTERVAL_MS)
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') void fetchNotifications()
    }
    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => {
      window.clearInterval(timer)
      document.removeEventListener('visibilitychange', onVisibilityChange)
      channel.close()
      channelRef.current = null
    }
  }, [cacheKey, channelName, fetchNotifications, storeData])

  const openNotification = async (item: AppNotification) => {
    if (!item.readAt) {
      const readAt = new Date().toISOString()
      const next = {
        ...data,
        unreadCount: Math.max(0, data.unreadCount - 1),
        items: data.items.map((row) => row.id === item.id ? { ...row, readAt } : row),
      }
      storeData(next)
      channelRef.current?.postMessage(next)
      try { await api.markNotificationRead(item.id) } catch { void fetchNotifications(true) }
    }
    setOpen(false)
    navigate(item.targetPath)
  }

  const markAllRead = async () => {
    if (!data.unreadCount) return
    const previous = data
    const readAt = new Date().toISOString()
    const next = {
      ...data,
      unreadCount: 0,
      items: data.items.map((item) => item.readAt ? item : { ...item, readAt }),
    }
    storeData(next)
    channelRef.current?.postMessage(next)
    setBusy(true)
    try {
      await api.markAllNotificationsRead()
      setMessage('')
    } catch (error) {
      storeData(previous)
      channelRef.current?.postMessage(previous)
      setMessage(error instanceof Error ? error.message : '全部標記已讀失敗。')
    } finally {
      setBusy(false)
    }
  }

  const updateOwnNotificationPreference = async (notifyOwnChanges: boolean) => {
    const previous = data
    const next = { ...data, preferences: { notifyOwnChanges } }
    storeData(next)
    channelRef.current?.postMessage(next)
    setBusy(true)
    try {
      await api.updateNotificationPreferences(notifyOwnChanges)
      setMessage('')
    } catch (error) {
      storeData(previous)
      channelRef.current?.postMessage(previous)
      setMessage(error instanceof Error ? error.message : '通知設定更新失敗。')
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <button
        type="button"
        className={`notification-bell ${isCollapsed ? 'collapsed' : ''}`}
        onClick={() => setOpen(true)}
        aria-label={`通知${data.unreadCount ? `，${data.unreadCount} 則未讀` : ''}`}
        title="通知"
      >
        <Bell size={isCollapsed ? 21 : 18} weight={data.unreadCount ? 'fill' : 'duotone'} />
        {!isCollapsed && <span>通知</span>}
        {data.unreadCount > 0 && <strong>{data.unreadCount > 99 ? '99+' : data.unreadCount}</strong>}
      </button>
      <Drawer
        open={open}
        title="通知"
        subtitle="顯示最近 50 則站內通知。"
        onClose={() => setOpen(false)}
        footer={(
          <div className="notification-footer">
            <Button onClick={() => void markAllRead()} disabled={busy || data.unreadCount === 0}><Checks size={16} />全部已讀</Button>
            <Button variant="primary" onClick={() => void fetchNotifications(true)} disabled={busy}><ArrowClockwise className={busy ? 'spin' : ''} size={16} />立即更新</Button>
          </div>
        )}
      >
        {message && <p className="notification-message">{message}</p>}
        {session.user.role === 'admin' ? (
          <label className="notification-preference">
            <span><strong>接收自己的修改通知</strong><small>關閉後只影響之後產生的通知。</small></span>
            <input
              type="checkbox"
              role="switch"
              checked={data.preferences.notifyOwnChanges}
              disabled={busy}
              onChange={(event) => void updateOwnNotificationPreference(event.target.checked)}
            />
          </label>
        ) : null}
        <div className="notification-list">
          {data.items.map((item) => (
            <button
              type="button"
              key={item.id}
              className={`notification-item ${item.readAt ? 'read' : 'unread'}`}
              onClick={() => void openNotification(item)}
            >
              <span className="notification-dot" aria-hidden="true" />
              <span className="notification-content">
                <strong>{item.title}</strong>
                <small>{notificationTime(item.createdAt)}{item.isTest ? ' · 測試通知' : ''}</small>
              </span>
            </button>
          ))}
          {!data.items.length && !busy && <div className="notification-empty"><Bell size={28} weight="duotone" /><strong>目前沒有通知</strong><span>有新的資料異動時會顯示在這裡。</span></div>}
          {!data.items.length && busy && <div className="notification-empty"><ArrowClockwise className="spin" size={26} /><span>正在載入通知…</span></div>}
        </div>
      </Drawer>
    </>
  )
}
